import React, { useState, useReducer, useEffect } from "react";
import {
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import { I18n } from "@aws-amplify/core";
import useMediaQuery from "@mui/material/useMediaQuery";
import currentUserToken from "CurrentUserToken";
import { APIStatusDialog } from "components/APIStatusDialog";
import dayjs from "dayjs";
import { DateTimeField } from '@mui/x-date-pickers';
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { post } from "aws-amplify/api";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).utc().format("YYYY-MM-DD HH:mm:ss");
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};

const SuspensionNote = ({ data, profile, fetchLists }) => {
  const isMobileDevice = useMediaQuery("(max-width:600px)");
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const formatTimestamp = (timestamp) => dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [isEditable, setIsEditable] = useState(false);
  const [notes, setNotes] = useState({
    process_note: "",
    suspension_end_datetime: "",
  });
  useEffect(() => {
    setNotes({
      process_note: data.process_note || "",
      suspension_end_datetime: data.suspension_end_datetime
        ? dayjs.utc(data.suspension_end_datetime).local().format("YYYY-MM-DD HH:mm:ss") // UTC -> Locale
        : "",
    });
  }, [data]);  
  
  const handleEditClick = () => setIsEditable(true);
  const handleCancelClick = () => {
    setIsEditable(false);
    setNotes({
      process_note: data.process_note || "",
      suspension_end_datetime: data.suspension_end_datetime
        ? dayjs(data.suspension_end_datetime).format("YYYY-MM-DD HH:mm:ss")
        : "",
    });
  };

  const handleSaveClick = async () => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Saving Suspension End Date"),
      });
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/alarm/suspension/edit",
        options: {
          body: {
            note: notes.process_note,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            alarm_id: data.id,
            end_date: notes.suspension_end_datetime ? dayjs(notes.suspension_end_datetime).utc().format("YYYY-MM-DD HH:mm:ss") : "",
          },
        },
      });
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Suspension End Date successfully saved"),
        });
        fetchLists();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to save Suspension End Date"),
        });
      }
      setIsEditable(false);
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to save Suspension End Date"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const handleNoteChange = (event, noteType) => {
    const updatedValue =
      noteType === "suspension_end_datetime"
        ? dayjs(event) // Usa direttamente l'oggetto dayjs
        : event.target.value;
  
    setNotes((prev) => ({ ...prev, [noteType]: updatedValue }));
  };
  
  const headerCellsNote = [
    { id: "suspension_end_datetime", label: I18n.get("Suspension End Date"), renderCell: (params) => formatTimestamp(params.value), },
  ];
  const isStateEditable = (data.state !== undefined && (Number(data.state) === 3 ));

  const hasProfilePermission = profile && (profile.includes("SuperAdmin") || profile.includes("Admin") || profile.includes("Operator"));
  const canEdit = hasProfilePermission && isStateEditable;

  return (
    <Card sx={{ width: "100%" }}>
      <CardContent sx={{padding: "1rem" }}>
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
        >
          {isEditable ? (
            <>
              <Tooltip title={I18n.get("Save Suspension End Date")}>
                <IconButton onClick={handleSaveClick}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={I18n.get("Cancel Edit")}>
                <IconButton onClick={handleCancelClick}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip
              title={
                canEdit
                  ? I18n.get("Edit Suspension End Date")
                  : "Edit Disabled - Not Suspended"
              }
            >
              <span>
                <IconButton
                  onClick={canEdit ? handleEditClick : null}
                  disabled={!canEdit}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        <TableContainer sx={{ width: "100%" }}>
          <Table sx={{ width: "100%" }}>
            <TableBody>
              {headerCellsNote.map((column, colIndex) => (
                <TableRow key={colIndex}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {column.label}:
                  </Typography>
                  {isEditable ? (
                    column.id === "suspension_end_datetime" ? (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimeField
                          value={notes.suspension_end_datetime ? dayjs(notes.suspension_end_datetime) : null}
                          onChange={(newValue) =>
                            setNotes((prev) => ({
                            ...prev,
                            suspension_end_datetime: newValue
                              ? dayjs(newValue).format("YYYY-MM-DD HH:mm:ss") // Mantieni il valore in locale per la visualizzazione
                              : "",
                          }))}
                          format="YYYY/MM/DD HH:mm"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                style: {
                                  padding: "4px",
                                  fontSize: "14px",
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>    
                    ) : column.id === "process_note" ? (
                      <input
                        type="text"
                        value={notes.process_note}
                        onChange={(e) => handleNoteChange(e, column.id)}
                        style={{
                          width: "100%",
                          padding: "4px",
                          fontSize: "14px",
                        }}
                      />
                    ) : (
                      <Typography variant="body2">
                        {notes[column.id] || ""}
                      </Typography>
                    )
                    ) : (
                      <Typography variant="body2">
                        {column.id === "suspension_end_datetime" && notes.suspension_end_datetime
                          ? dayjs(notes.suspension_end_datetime).format("YYYY-MM-DD HH:mm:ss")
                          : notes[column.id] || ""}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </Card>
  );

};

export default SuspensionNote;
